<template>
    <div>
        <div v-if="($page.props.acl.isAdmin && item.migrator)" class="grid grid-cols-3 gap-2 my-1 bg-gray-100 p-1">
            <div class="col-span-1 text-left pl-2">
                <strong>Article transféré par:</strong> {{ item.migrator.name }}
            </div>
            <div class="col-span-2 text-left pl-2">
                <strong>Motif du transfert:</strong> {{ item.migrate_reason }}
            </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
            <div class="flex flex-col col-span-full sm:col-span-5">
                <button @click.prevent="manageFilesModal = true"
                    class="shadow-lg btn hover:bg-primaryOrange-300 bg-primaryOrange text-gray-800 hover:text-white py-1 px-2 mb-2 uppercase"
                >
                    <i class="fad fa-camera"></i><span class="ml-2">Images de l'article</span>
                </button>
                <div class="space-y-2">
                    <h2 v-if="!item.media.length" class="shadow-lg flex justify-between font-semibold p-2 border-b border-gray-200 text-center bg-red-100 text-red-500 italic">
                        Aucune image enregistrée pour cet article.
                    </h2>
                    <div class="bg-slate-200 p-2 text-left">
                        <div class="font-bold">Nom de l'article:</div>
                        <textarea class="w-full bg-slate-200 text-sm border-none px-0" cols="24" rows="4" disabled :value="item.name"></textarea>
                    </div>
                    <button @click.prevent="showHelpModal()" class="w-full text-center shadow-lg btn hover:bg-primaryOrange-300 bg-primaryOrange text-gray-800 hover:text-white py-1 px-2 mb-2 uppercase">
                        Cliquez pour plus de détails
                    </button>
                </div>
            </div>

            <div class="col-span-full sm:col-span-7 shadow-lg rounded-sm border border-gray-200">
                <div>
                    <h2 class="flex justify-between bg-primaryOrange text-gray-800 font-semibold px-2 py-1 text-left">
                        Informations de la vente de l'article {{ item.reference }}
                    </h2>
                    <div class="p-2 border border-gray-200 rounded-sm shadow-sm">
                        <div class="sm:flex sm:justify-between">
                            <div class="grow text-left font-semibold text-gray-500">
                                <div><b>Prix:</b> {{ formatAmount(item.price, currency) }}</div>
                            </div>
                            <div class="grow text-left font-semibold text-gray-500">
                                <div><b>Total versé:</b> {{ formatAmount(item.total_paid, currency) }}</div>
                            </div>
                            <div class="grow text-left font-semibold text-gray-500">
                                <div><b>Total restant:</b> {{ formatAmount(item.balance, currency) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <h2 class="flex justify-between bg-primaryOrange text-gray-800 font-semibold px-2 py-1 text-left">
                        Historique des paiements
                        <template v-if="item.balance > 0">
                            <button class="btn bg-white text-primaryOrange hover:bg-primaryOrange-300 hover:text-white p-1" @click.prevent="showModal">
                                <svg class="h-3 w-3 fill-current shrink-0" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </template>
                    </h2>
                    <div class="overflow-x-auto">
                        <table class="w-full">
                            <thead class="text-xs font-semibold uppercase text-gray-500 bg-gray-50 border-t border-b border-gray-200">
                                <tr>
                                    <th>Référence</th>
                                    <th>Montant</th>
                                    <th>Mode de paiement</th>
                                    <th>Date</th>
                                    <th>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody class="text-xs divide-y divide-gray-200">
                                <tr v-if="!item.payments.length">
                                    <td colspan="5" class="bg-red-100 text-xl text-center py-10 text-red-500">
                                        Aucun paiement enregistré pour le moment.
                                    </td>
                                </tr>
                                <tr v-else v-for="(payment, index) in item.payments" :key="index">
                                    <td>
                                        <span class="text-primaryOrange font-semibold">{{ payment.reference }}</span>
                                    </td>
                                    <td>
                                        <div class="text-right">{{ formatAmount(payment.amount, currency) }}</div>
                                    </td>
                                    <td>{{ payment.mode_real_text }}</td>
                                    <td class="capitalize">{{ formattedDate(payment.created_at) }}</td>
                                    <td class="text-right">
                                        <i @click.prevent="printPayment(payment)"
                                            class="fad fa-print text-sm cursor-pointer text-greendack hover:text-greendack-300"
                                        ></i>
                                        <button v-if="item.acl_list.canDeleteItemPayment && payment.id === item.lastPaymentId"
                                            class="text-red-500 hover:text-red-600 ml-3 disabled:opacity-50" @click.prevent="destroyPayment(payment)"
                                        ><i class="fad fa-trash text-sm"></i>
                                        </button>
                                        <i v-else title="Impossible de supprimer"
                                            class="fad fa-trash text-sm text-gray-500 opacity-50 cursor-not-allowed ml-3"
                                        ></i>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <JetDialogModal :show="manageModal" @close="closeModal" max-width="2xl" backgroundClass="bg-primaryOrange" textClass="text-gray-800">
        <template #title>Ajouter un paiement à l'article: N°{{ item.reference }}</template>

        <template #content>
            <div class="grid grid-cols-2 gap-4 pb-2">
                <div v-if="! item.customer_id" class="col-span-full">
                    <div>
                        <ul class="text-sm font-medium flex flex-nowrap overflow-x-scroll no-scrollbar">
                            <li class="pb-3 mr-6 last:mr-0 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                                <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 1"
                                    :class="currentTab === 1 ? 'text-primaryOrange font-semibold' : 'text-gray-500 hover:text-gray-600'"
                                >Client existant</a>
                            </li>
                            <li class="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                                <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 2"
                                    :class="currentTab === 2 ? 'text-primaryOrange font-semibold' : 'text-gray-500 hover:text-gray-600'"
                                >Nouveau Client</a>
                            </li>
                        </ul>
                    </div>

                    <transition
                        enter-active-class="transition ease-out duration-200 transform"
                        enter-from-class="opacity-0 -translate-y-2"
                        enter-to-class="opacity-100 translate-y-0"
                        leave-active-class="transition ease-out duration-200"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <div class="mb-4">
                            <hr>
                            <h2 class="text-md text-gray-800 font-semibold bg-primaryOrange px-2 mt-3">
                                Informations du {{ currentTab === 2 ? 'nouveau' : '' }} client {{ currentTab === 1 ? 'existant' : '' }}
                            </h2>
                            <hr>
                            <div v-show="currentTab === 1">
                                <div class="grid grid-cols-2 gap-4 mt-2">
                                    <div class="col-span-2 sm:col-span-2">
                                        <JetLabelRequired for="customer_id" value="Client" />
                                        <Multiselect
                                            id="customer_id"
                                            :delay="0"
                                            :minChars="1"
                                            :searchable="true"
                                            :valueProp="'id'"
                                            :trackBy="'id'"
                                            :filterResults="false"
                                            :resolveOnLoad="true"
                                            v-model="form.customer_id"
                                            placeholder="Tapez pour rechercher le client"
                                            :options="async function(query) {
                                                return await fetchUser(query)
                                            }"
                                        >
                                            <template v-slot:singlelabel="{ value }">
                                                <div class="multiselect-single-label text-sky">
                                                    {{ value.name + (value.email ? ' | ' + value.email : '') + (value.full_phone ? ' | ' + value.full_phone : '') }}
                                                </div>
                                            </template>

                                            <template v-slot:option="{ option }">
                                                {{ option.name + (option.email ? ' | ' + option.email : '') + (option.full_phone ? ' | ' + option.full_phone : '') }}
                                            </template>
                                        </Multiselect>
                                        <JetInputError :message="form.errors.customer_id" />
                                    </div>
                                </div>
                            </div>
                            <div v-show="currentTab === 2">
                                <div class="grid grid-cols-2 gap-4 mt-2">
                                    <div class="col-span-2">
                                        <JetLabelRequired for="customer_name" value="Nom du client" />
                                        <JetInput id="customer_name" type="text" autocomplete="off"
                                            v-model="form.customer_name" placeholder="Entrez le nom du Client"
                                        />
                                        <JetInputError :message="form.errors.customer_name" />
                                    </div>
                                    <div class="col-span-1">
                                        <JetLabel for="customer_phone" value="Téléphone du client:" />
                                        <vue-tel-input required @input="customerPhoneInput"
                                            :defaultCountry="form.customer_country_code"
                                            :value="form.customer_phone"
                                        />
                                        <JetInputError :message="form.errors.phone" />
                                    </div>
                                    <div class="col-span-1">
                                        <JetLabel for="customer_email" value="Email du client:" />
                                        <JetInput id="customer_email" type="text" autocomplete="off"
                                            v-model="form.customer_email" placeholder="Entrez l'email du client"
                                        />
                                        <JetInputError :message="form.errors.customer_email" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                    <hr>
                </div>

                <h2 class="col-span-full text-md text-gray-800 font-semibold bg-primaryOrange px-2">
                    Informations de paiement
                </h2>
                <div class="col-span-1">
                    <JetLabel for="amount">
                        Montant du versement en {{ currency.symbol }} (<a href="#" @click="form.amount = item.balance" class="underline text-primaryOrange">Solder</a>)(<span class="text-red-500 text-sm">*</span>):
                    </JetLabel>
                    <JetInput id="amount" type="number" min="0" placeholder="O" class="text-right" v-model="form.amount" />
                    <JetInputError :message="form.errors.amount" />
                </div>
                <div class="col-span-1">
                    <JetLabelRequired value="Mode de paiement" />
                    <select v-model="form.mode" class="form-input w-full uppercase">
                        <option value="null" disabled selected hidden>Choisissez le mode de paiement</option>
                        <option v-for="(mode, index) in modes" :key="index" :value="mode['value']">
                            {{ mode['description'] }}
                        </option>
                    </select>
                    <JetInputError :message="form.errors.mode" />
                </div>
            </div>
        </template>

        <template #footer>
            <BotiButton @click="closeModal" text="Quitter" color="red">
                <template #icon><i class="fad fa-times-circle text-xs ml-1"></i></template>
            </BotiButton>

            <BotiButton :loader="form.processing" :disabled="form.processing"
                @click="storePayment" text="Enregistrer" class="ml-2" :class="{ 'opacity-25': form.processing }"
            >
                <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                </svg>
                <template #icon>
                    <i class="fad fa-check-circle text-xs ml-1"></i>
                </template>
            </BotiButton>
        </template>
    </JetDialogModal>

    <JetDialogModal :show="manageFilesModal" @close="manageFilesModal = false" max-width="xl" backgroundClass="bg-primaryOrange">
        <template #title>Ajouter des images à l'article: N°{{ item.reference }}</template>

        <template #content>
            <div class="grid grid-cols-2 gap-4">
                <div class="col-span-full">
                    <UploadFilesBox
                        :route="route('travels.items.upload-files', { travel: travel.reference, item: item.slug })"
                        :media="item.media"
                    />
                </div>
            </div>
        </template>

        <template #footer>
            <BotiButton @click.prevent="manageFilesModal = false" text="Quitter" color="red">
                <template #icon><i class="fad fa-times-circle text-xs ml-1"></i></template>
            </BotiButton>
        </template>
    </JetDialogModal>

    <JetDialogModal :show="manageHelpModal" @close="closeHelpModal" max-width="2xl" backgroundClass="bg-primaryOrange">
        <template #title>Détails article: N°{{ item.reference }}</template>
        
        <template #content>
            <div class="flex flex-col space-y-4 px-2">
                <p><b>Nom:</b> {{ item.name }}</p>
                <div><b>Prix:</b> {{ formatAmount(item.price, currency) }}</div>
                <div>
                    <b>Catégories:</b>
                    <span v-if="!item.categories.length" class="ml-2">Non renseignée</span>
                    <span v-else v-for="category in item.categories" :key="category.id"
                        class="ml-2 text-xs bg-primaryOrange-100 text-primaryOrange-600 px-2 py-1 rounded-full"
                    >{{ category.name }}</span>
                </div>
                <div>
                    <div class="font-bold">Description:</div>
                    <div class="w-full p-2 bg-gray-100 border-none text-sm" v-html="item.description"></div>
                </div>
            </div>
        </template>
        <template #footer>
            <BotiButton @click="closeHelpModal" text="Fermer" color="red">
                <template #icon><i class="fad fa-times-circle text-sm ml-1"></i></template>
            </BotiButton>
        </template>
    </JetDialogModal>
</template>

<script>
    import { defineComponent } from 'vue'
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import useDevice from '@/composables/device'
    import BotiButton from '@/Components/Button'
    import Multiselect from '@vueform/multiselect'
    import JetInputError from '@/Jetstream/InputError'
    import PrintMixins from '@/Mixins/PrintMixins.vue'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import FetchUserMixins from "@/Mixins/FetchUserMixins"
    import UploadFilesBox from '@/Components/UploadFilesBox'
    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    import JetLabelRequired from '@/Jetstream/LabelRequired'
    import FormValidatorErrorMixinsVue from '@/Mixins/FormValidatorErrorMixins.vue'

    export default defineComponent({
        components: {
            JetLabel,
            JetInput,
            BotiButton,
            Multiselect,
            JetInputError,
            JetDialogModal,
            UploadFilesBox,
            JetLabelRequired
        },

        props: {
            item: Object,
            modes: Array,
            travel: Object,
            currency: Object
        },

        setup() {
            const { isMobile, isTablet, isDesktop } = useDevice()

            return {
                isMobile,
                isTablet,
                isDesktop
            }
        },

        mixins: [FetchUserMixins, PrintMixins, FormValidatorErrorMixinsVue, CurrencyMixins, DateFormatMixins],

        data () {
            return {
                manageModal: false,
                manageFilesModal: false,
                manageHelpModal: false,
                currentTab: 1,
                currentItem: null,
                form: this.$inertia.form({
                    customer_name: null,
                    customer_email: null,
                    customer_phone: null,
                    customer_dial_code: null,
                    customer_country_code: null,
                    customer_id: null,
                    mode: null,
                    amount: null
                }),
            }
        },

        methods: {
            customerPhoneInput(number, phoneObject) {
                if(number !== undefined && phoneObject !== undefined) {
                    this.form.customer_phone =  number ? number : ''
                    this.form.customer_dial_code = phoneObject ? phoneObject.country.dialCode : null
                    this.form.customer_country_code = phoneObject ? phoneObject.country.iso2 : null
                }
            },

            closeModal() {
                this.manageModal = false
                this.form.clearErrors()
                this.form.reset()
            },

            showModal() {
                this.form.clearErrors()
                this.manageModal = true
            },

            closeHelpModal() {
                this.manageHelpModal = false
            },

            showHelpModal(currentItem) {
                this.manageHelpModal = true
            },

            storePayment() {
                this.form
                    .transform((data) => ({
                        ...data,
                        type: this.currentTab,
                    }))
                    .post(route('items.payments.store', this.item), {
                        preserveScroll: true,
                        onSuccess: () =>{
                            this.closeModal()
                            this.$swal.fire(
                                'Paiement',
                                'Paiement ajouté avec succès.',
                                'success'
                            )
                        },
                    })
            },

            printPayment(payment) {
                const url = route('items.payments.print', {
                    item: this.item,
                    payment: payment
                })

                if (this.isMobile || this.isTablet) {
                    // 📱 MOBILE → PREVIEW
                    const link = document.createElement('a')
                    link.href = url
                    // link.target = '_blank'
                    link.click()
                } else {
                    // 💻 DESKTOP → NEW TAB
                    this.print(url)
                }
            },

            destroyPayment(payment) {
                this.$swal.queue([{
                    title: "Paiement",
                    text: "Êtes vous sûr de vouloir supprimer le paiement ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Supprimer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.delete(route('items.payments.destroy', {
                            item: this.item,
                            payment: payment
                        }), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire({
                                    icon: 'success',
                                    title: "Paiement",
                                    text: "Paiement supprimé avec succès.",
                                })
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.message || 'Oups! Impossible de supprimer le paiement.',
                                })
                            }
                        })
                    }
                }])
            }
        }
    })
</script>

<style scoped>
    tbody :deep(tr:hover td) {
        --tw-bg-opacity: 1;
        background-color: rgba(225, 225, 225, var(--tw-bg-opacity))!important
    }
</style>
